import React, { useEffect, useState } from "react";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { MONTHS_ENUM } from "./Constants";
import * as dayjs from "dayjs";
import * as utc from "dayjs/plugin/utc";
import { ActivityType } from "../pages/Masters/Master.constants";
import FormRenderer, { ComponentMapper, FormTemplate, componentTypes } from "./FormRenderer";
import { getMaxMonthYear, getMinMonthYear } from "../../utils/common";
import { ACTIVITY_TYPE, API_DELIMITER, DEBOUNCE_TIME } from "../../utils/constants";
import { debounce } from "underscore";
import { getActivities, getActs, getRules } from "../../backend/masters";
import { DEFAULT_OPTIONS_PAYLOAD } from "./Table";
import { activityOptionLabel, ruleOptionLabel } from "../pages/Masters/Mappings/MappingDetails";
dayjs.extend(utc as any);

function AdvanceSearchModal(this: any, { data, onSubmit, onCancel, todoType }: any) {
    const [filter, setFilter] = useState<any>({ hideButtons: true, ...data });
    const [acts, setActs] = useState<any[]>([]);
    const [rules, setRules] = useState<any[]>([]);
    const [activities, setActivities] = useState<any[]>([]);

    function actOptionLabel({ label, act = {} }: any) {
        return (
            <div className="d-flex flex-column">
                <div>{label} - <span className="fw-bold ms-1">{act.establishment_type}</span></div> 
            </div>
        )
    }

    const schema: any = todoType && todoType === "location" ? {
        fields: [
            {
                component: componentTypes.MONTH_PICKER,
                name: 'monthYear',
                label: 'Month & Year',
                className: 'grid-col-50',
                minDate: getMinMonthYear(),
                maxDate: getMaxMonthYear(),
                range: false,
                clearable: true,
                initialValue: filter.monthYear,
                onChange: onMonthYearChange.bind(this)
            },
            {
                component: componentTypes.DATE_PICKER,
                name: 'dueDate',
                label: 'Due Date',
                className: 'grid-col-100',
                range: true,
                clearable: true,
                initialValue: filter.dueDate,
                onChange: onDueDateChange.bind(this)
            },
            {
                component: componentTypes.SELECT,
                name: 'activityType',
                label: 'Activity Type',
                options: ActivityType,
                initialValue: filter.activity_activity_type_in || [],
                isMulti: true
            },
            {
                component: componentTypes.SELECT,
                name: 'auditType',
                label: 'Audit Type',
                options: Object.values(ACTIVITY_TYPE),
                isMulti: true
            },
            {
                component: componentTypes.ASYNC_SELECT,
                name: 'act',
                label: 'Act',
                defaultOptions: acts,
                formatOptionLabel: actOptionLabel,
                loadOptions: debounce((keyword: any, callback: any) => {
                    getActs({ ...DEFAULT_OPTIONS_PAYLOAD, filters: {name_cont: keyword} }).then(response => {
                        const list = ((response || {}).data || {}).items || [];
                        const _options = list.map((act: any) => {
                            return { value: act.id, label: act.name, act }
                        });
                        setActs(_options);
                        callback(_options);
                    })
                }, DEBOUNCE_TIME),
                isClearable: true
            },
            {
                component: componentTypes.ASYNC_SELECT,
                name: 'rule',
                label: 'Rule',
                defaultOptions: rules,
                formatOptionLabel: ruleOptionLabel,
                loadOptions: debounce((keyword: any, callback: any) => {
                    getRules({ ...DEFAULT_OPTIONS_PAYLOAD, page: 1, per: 100, filters: {name_cont: keyword} }).then(response => {
                        const list = ((response || {}).data || {}).items || [];
                        const _options = list.map((rule: any) => {
                            return { value: rule.id, label: rule.name, rule }
                        });
                        setRules(_options);
                        callback(_options);
                    })
                }, DEBOUNCE_TIME),
                isClearable: true
            },
            {
                component: componentTypes.ASYNC_SELECT,
                name: 'activity',
                label: 'Activity',
                formatOptionLabel: activityOptionLabel,
                defaultOptions: activities,
                loadOptions: debounce((keyword: any, callback: any) => {
                    getActivities({ ...DEFAULT_OPTIONS_PAYLOAD, filters: {name_cont: keyword} }).then(response => {
                        const list = ((response || {}).data || {}).items || [];
                        const _options = list.map((activity: any) => {
                            return { value: activity.id, label: activity.name, activity }
                        });
                        setActivities(_options);
                        callback(_options);
                    })
                }, DEBOUNCE_TIME),
                isClearable: true
            }
        ]
    } :
    {
        fields: [
            {
                component: componentTypes.MONTH_PICKER,
                name: 'monthYear',
                label: 'Month & Year',
                className: 'grid-col-50',
                minDate: getMinMonthYear(),
                maxDate: getMaxMonthYear(),
                range: false,
                clearable: true,
                initialValue: filter.monthYear,
                onChange: onMonthYearChange.bind(this)
            },
            {
                component: componentTypes.DATE_PICKER,
                name: 'dueDate',
                label: 'Due Date',
                className: 'grid-col-100',
                range: true,
                clearable: true,
                initialValue: filter.dueDate,
                onChange: onDueDateChange.bind(this)
            },
            {
                component: componentTypes.SELECT,
                name: 'activityType',
                label: 'Activity Type',
                options: ActivityType,
                initialValue: filter.activity_activity_type_in || [],
                isMulti: true
            },
            {
                component: componentTypes.SELECT,
                name: 'auditType',
                label: 'Audit Type',
                options: Object.values(ACTIVITY_TYPE),
                isMulti: true
            }
        ]
    }

    function debugForm(_form: any) {
        setFilter(_form.values);
    }

    function isInvalid() {
        return false;
    }

    function clearFilter() {
        onSubmit({});
        onCancel();
    }

    function onMonthYearChange(event: any) {
        setFilter({ ...filter, monthYear: event ? event.toDate() : undefined })
    }

    function onDueDateChange(event: any) {
        setFilter({ ...filter, dueDate: event })
    }

    function search() {
        const { monthYear, dueDate, activityType, auditType, act, rule, activity } = filter;
        const _payload: any = {};
        if (monthYear) {
            const date = new Date(monthYear);
            const month = MONTHS_ENUM[date.getMonth()];
            const year = date.getFullYear();
            _payload.month_eq = month;
            _payload.year_eq = `${year}`;
        }

        if (dueDate) {
            let fromDate, toDate;
            if (Array.isArray(dueDate)) {
                if(dueDate.length === 2){
                    fromDate = new Date(dueDate[0]);
                    toDate = new Date(dueDate[1]);
                } else {
                    toDate = new Date(dueDate[0]);
                }
            }
            if(fromDate && toDate) {
                _payload.due_date_gteq = dayjs(fromDate).startOf('D').local().format();
                _payload.due_date_lteq = dayjs(toDate).endOf('D').local().format();
            } else if (toDate) {
                _payload.due_date_lteq = dayjs(toDate).endOf('D').local().format();
            }
            // _payload.fromDate = dayjs(new Date(fromDate)).startOf('D').toISOString();
            // _payload.toDate = dayjs(new Date(toDate)).endOf('D').toISOString();
        }

        if (activityType && activityType.length > 0) {
            _payload.activity_activity_type_in = activityType.map((x: any) => x.value);
        }
        if (auditType) {
            _payload.auditted_in = auditType.map((x: any) => x.value);
        }
        if(act) {
            _payload.act_id_eq = act.value;
        }
        if(rule) {
            _payload.rule_id_eq = rule.value;
        }
        if(activity) {
            _payload.activity_id_eq = activity.value;
        }
        onSubmit({ payload: _payload, data: filter });
        onCancel();
    }

    return (
        <Modal show={true} backdrop="static" animation={false} dialogClassName="drawer" size="lg">
            <Modal.Header closeButton={true} onHide={onCancel}>
                <Modal.Title className="bg">Advance Search</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex justify-content-start">
                    <div className="col-12">
                        <form>
                            <div className="row mt-3">
                                <div className="col-12">
                                    <FormRenderer FormTemplate={FormTemplate}
                                        initialValues={filter}
                                        componentMapper={ComponentMapper}
                                        schema={schema}
                                        debug={debugForm}
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <Button variant="outline-secondary" onClick={onCancel} className="btn btn-outline-secondary">
                    Back
                </Button>
                <div>
                    <Button variant="primary" onClick={clearFilter} className="mx-3">Clear</Button>
                    <Button variant="primary" onClick={search} disabled={isInvalid()}>Submit</Button>
                </div>
            </Modal.Footer>
        </Modal>
    )
}

export default AdvanceSearchModal;